import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import MatchInfoDialog from "@/components/MatchInfoDialog";
import EventPhotos from "@/components/EventPhotos";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/dateFormat";
import {
  ArrowLeft,
  Calendar,
  Target,
  Trophy,
  Clock,
  Shield,
  MapPin,
  Printer,
  CalendarPlus,
  Pencil,
  Info,
} from "lucide-react";

const API = "/api";

function parseDT(s) {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatDayHeading(d) {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeShort(dt) {
  const d = parseDT(dt);
  if (!d) return "TBD";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// Turn http(s) URLs in description into clickable links (preserves newlines)
function linkify(text) {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p)) {
      return (
        <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-[#1B3B6F] underline hover:opacity-80 break-words">
          {p}
        </a>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function EventInfo() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { settings } = useSettings();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEvent = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}?t=${Date.now()}`);
      setEvent(res.data);
    } catch (e) {
      console.error("Failed to load event", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const grouped = useMemo(() => {
    const matches = event?.matches || [];
    const groups = new Map();
    const noTime = [];
    matches.forEach((m) => {
      const d = parseDT(m.start_time);
      if (!d) {
        noTime.push(m);
        return;
      }
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!groups.has(key)) groups.set(key, { date: d, items: [] });
      groups.get(key).items.push(m);
    });
    groups.forEach((g) => {
      g.items.sort((a, b) => (parseDT(a.start_time)?.getTime() ?? 0) - (parseDT(b.start_time)?.getTime() ?? 0));
    });
    return { sorted: Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b)), noTime };
  }, [event]);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Loading event…</div>;
  }
  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-700">Event not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      </div>
    );
  }

  const totalMatches = (event.matches || []).length;
  const subscribeUrl =
    window.location.origin + "/api/calendar.ics?event_id=" + encodeURIComponent(event.id);
  const webcalUrl = subscribeUrl.replace(/^https?:\/\//, "webcal://");

  return (
    <div data-testid="event-info-page" className="space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
          data-testid="event-info-back-btn"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      {/* Hero */}
      <Card className="overflow-hidden">
        <div
          className="relative text-white p-6 sm:p-8"
          style={
            event.cover_photo_url
              ? {
                  backgroundImage: `linear-gradient(rgba(27,59,111,0.65), rgba(27,59,111,0.85)), url(${event.cover_photo_url})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : { backgroundColor: "#1B3B6F" }
          }
          data-testid="event-info-hero"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-white/70">
                {settings.organization_name || "Event"}
              </p>
              <h1 className="font-serif text-3xl lg:text-4xl font-bold mt-1">{event.name}</h1>
              <p className="text-white/90 mt-2 flex items-center gap-2 text-sm">
                <Calendar size={16} />
                {formatDate(event.start_date)}
                {event.end_date && event.end_date !== event.start_date ? ` – ${formatDate(event.end_date)}` : ""}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Badge className="bg-white/20 text-white">{totalMatches} matches</Badge>
                {event.status && (
                  <Badge className="bg-white/20 text-white capitalize">{event.status}</Badge>
                )}
                {parseFloat(event.registration_fee) > 0 && isAdmin() && (
                  <Badge className="bg-white/20 text-white">
                    Reg. Fee: ${parseFloat(event.registration_fee).toFixed(2)}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <Link to={`/print-schedule/${event.id}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white w-full">
                  <Printer size={14} className="mr-1" /> Print Schedule
                </Button>
              </Link>
              <a href={webcalUrl} className="block">
                <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white w-full">
                  <CalendarPlus size={14} className="mr-1" /> Subscribe
                </Button>
              </a>
              {isAdmin() && (
                <Link to={`/matches/${event.id}`}>
                  <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white w-full">
                    <Pencil size={14} className="mr-1" /> Manage
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Description */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Info className="text-[#1B3B6F]" />
            About this event
          </CardTitle>
        </CardHeader>
        <CardContent>
          {event.description ? (
            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
              {linkify(event.description)}
            </p>
          ) : (
            <p className="text-slate-500 italic text-sm">
              No description provided.
              {isAdmin() && (
                <>
                  {" "}
                  <Link to="/events" className="text-[#1B3B6F] underline">Add one from Events.</Link>
                </>
              )}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <EventPhotos eventId={event.id} onCoverChanged={loadEvent} />

      {/* Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Calendar className="text-[#1B3B6F]" />
            Match Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          {totalMatches === 0 ? (
            <p className="text-center text-slate-500 py-6">No matches scheduled for this event yet.</p>
          ) : (
            <div className="space-y-5">
              {grouped.sorted.map(([key, g]) => (
                <div key={key}>
                  <h3 className="font-serif text-lg font-semibold border-b border-slate-200 pb-1 mb-2">
                    {formatDayHeading(g.date)}
                  </h3>
                  <div className="divide-y divide-slate-100">
                    {g.items.map((m) => (
                      <div key={m.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col items-center justify-center min-w-[68px] bg-[#1B3B6F]/10 text-[#1B3B6F] rounded-lg px-2 py-2">
                            <Clock size={12} />
                            <span className="font-mono font-semibold text-xs mt-1">{formatTimeShort(m.start_time)}</span>
                          </div>
                          <div className={`p-2 rounded-lg ${m.is_protection ? "bg-purple-100" : "bg-green-100"} self-center`}>
                            {m.is_protection ? <Shield size={16} className="text-purple-600" /> : <Target size={16} className="text-green-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              <MatchInfoDialog match={m} eventName={event?.name} prefix />
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                              {m.is_protection && (
                                <Badge variant="outline" className="text-purple-600">Protection</Badge>
                              )}
                              <span>{m.shots_per_round} shots</span>
                              {m.trap_number && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={10} /> Trap {m.trap_number}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Link to={`/leaderboard/${m.id}`}>
                          <Button variant="outline" size="sm">
                            <Trophy size={14} className="mr-1" /> Leaderboard
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {grouped.noTime.length > 0 && (
                <div>
                  <h3 className="font-serif text-lg font-semibold border-b border-slate-200 pb-1 mb-2">
                    Time TBD
                  </h3>
                  <ul className="divide-y divide-slate-100">
                    {grouped.noTime.map((m) => (
                      <li key={m.id} className="py-2 text-sm flex items-center justify-between">
                        <span className="font-medium text-slate-800">{m.name}</span>
                        <span className="text-xs text-slate-500">
                          {m.shots_per_round ? `${m.shots_per_round} shots` : ""}
                          {m.trap_number ? ` • Trap ${m.trap_number}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
